import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Settings, Award, Plus, Edit, Trash2, Search, 
  LayoutDashboard, LogOut, FileText, Users, Mail, Building2, UserCheck, Clock, Bell, Check, X, Briefcase, Shield, Download, Upload
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Backoffice = () => {
  const { token, logout, isAdmin, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [competencies, setCompetencies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [itTalents, setItTalents] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const importFileInputRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [availabilityOptions, setAvailabilityOptions] = useState([]);
  const [itFunctions, setItFunctions] = useState([]);
  const [newFunctionPt, setNewFunctionPt] = useState('');
  const [newFunctionEn, setNewFunctionEn] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editComp, setEditComp] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [editAvailability, setEditAvailability] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState('competency'); // 'competency', 'recruiter', 'it_talent', 'client', 'availability'
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAddRecruiterDialog, setShowAddRecruiterDialog] = useState(false);
  const [showAddITTalentDialog, setShowAddITTalentDialog] = useState(false);
  const [showAddAdminDialog, setShowAddAdminDialog] = useState(false);
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [showAddAvailabilityDialog, setShowAddAvailabilityDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('competencies');
  const [newComp, setNewComp] = useState({ name_pt: '', name_en: '', category: 'technical', keywords: '' });
  const [newRecruiterEmail, setNewRecruiterEmail] = useState('');
  const [newITTalentEmail, setNewITTalentEmail] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newAvailability, setNewAvailability] = useState({ name_pt: '', name_en: '', order: 0 });
  const [pendingRequests, setPendingRequests] = useState([]);
  const [renameInput, setRenameInput] = useState({});

  const lang = language || 'pt';

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchCompetencies(), fetchRecruiters(), fetchItTalents(), fetchAdmins(), fetchClients(), fetchAvailabilityOptions(), fetchPendingRequests(), fetchItFunctions(), fetchDuplicateGroups()]);
    setLoading(false);
  };

  const fetchDuplicateGroups = async () => {
    try {
      const response = await axios.get(`${API}/competencies/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicateGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error fetching duplicate groups:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/competencies/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, 'evolutt_competencias_template.xlsx');
      toast.success(lang === 'pt' ? 'Template descarregado' : 'Template downloaded');
    } catch (error) {
      toast.error(lang === 'pt' ? 'Erro ao descarregar template' : 'Failed to download template');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so the same file can be re-selected later
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/competencies/import`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const r = response.data;
      const summary = lang === 'pt'
        ? `Importação: ${r.counts.created} criadas, ${r.counts.duplicates} duplicados ignorados, ${r.counts.errors} erros`
        : `Import: ${r.counts.created} created, ${r.counts.duplicates} duplicates skipped, ${r.counts.errors} errors`;
      if (r.counts.duplicates > 0 || r.counts.errors > 0) {
        toast.warning(summary, {
          duration: 15000,
          description: r.duplicates.slice(0, 5).map(d =>
            `${lang === 'pt' ? 'Linha' : 'Row'} ${d.row}: "${d.name_pt}" → ${d.conflicts_with} (${d.where})`
          ).join('\n')
        });
      } else {
        toast.success(summary);
      }
      fetchCompetencies();
      fetchDuplicateGroups();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message;
      toast.error(`${lang === 'pt' ? 'Erro ao importar' : 'Import failed'}: ${detail}`, { duration: 12000 });
    }
  };

  const fetchCompetencies = async () => {
    try {
      const response = await axios.get(`${API}/competencies`);
      setCompetencies(response.data);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get(`${API}/competency-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingRequests(response.data);
    } catch (error) {
      // Non-admin users get 403/401 here — that's expected, just log for visibility.
      if (error?.response?.status && ![401, 403].includes(error.response.status)) {
        console.warn('fetchPendingRequests failed:', error?.response?.status, error?.response?.data);
      }
    }
  };

  const fetchRecruiters = async () => {
    try {
      const response = await axios.get(`${API}/recruiters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruiters(response.data);
    } catch (error) {
      console.error('Error fetching recruiters:', error);
    }
  };

  const fetchItTalents = async () => {
    try {
      const response = await axios.get(`${API}/it-talents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItTalents(response.data);
    } catch (error) {
      console.error('Error fetching IT talents:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAvailabilityOptions = async () => {
    try {
      const response = await axios.get(`${API}/availability-options`);
      setAvailabilityOptions(response.data);
    } catch (error) {
      console.error('Error fetching availability options:', error);
    }
  };

  const fetchItFunctions = async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      setItFunctions(response.data);
    } catch (error) {
      console.error('Error fetching IT functions:', error);
    }
  };

  const handleAddFunction = async () => {
    if (!newFunctionPt.trim()) {
      toast.error(lang === 'pt' ? 'Nome PT é obrigatório' : 'PT name is required');
      return;
    }
    try {
      await axios.post(`${API}/functions`, {
        name_pt: newFunctionPt.trim(),
        name_en: newFunctionEn.trim() || newFunctionPt.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewFunctionPt('');
      setNewFunctionEn('');
      fetchItFunctions();
      toast.success(lang === 'pt' ? 'Função adicionada' : 'Function added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleDeleteFunction = async (funcId) => {
    try {
      await axios.delete(`${API}/functions/${funcId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchItFunctions();
      toast.success(lang === 'pt' ? 'Função eliminada' : 'Function deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    }
  };

  const handleAddCompetency = async () => {
    if (!newComp.name_pt || !newComp.name_en) {
      toast.error(lang === 'pt' ? 'Preencha todos os campos' : 'Fill all fields');
      return;
    }
    try {
      const keywords = newComp.keywords ? newComp.keywords.split(',').map(k => k.trim()).filter(k => k) : [];
      await axios.post(`${API}/competencies`, {
        ...newComp,
        keywords
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setShowAddDialog(false);
      setNewComp({ name_pt: '', name_en: '', category: 'technical', keywords: '' });
      fetchCompetencies();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleUpdateCompetency = async () => {
    if (!editComp) return;
    try {
      const keywords = typeof editComp.keywords === 'string' 
        ? editComp.keywords.split(',').map(k => k.trim()).filter(k => k) 
        : editComp.keywords || [];
      await axios.put(`${API}/competencies/${editComp.id}`, {
        name_pt: editComp.name_pt,
        name_en: editComp.name_en,
        category: editComp.category,
        keywords
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setEditComp(null);
      fetchCompetencies();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      if (deleteType === 'competency') {
        await axios.delete(`${API}/competencies/${deleteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCompetencies();
      } else if (deleteType === 'recruiter') {
        await axios.delete(`${API}/recruiters/${deleteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchRecruiters();
      } else if (deleteType === 'it_talent') {
        await axios.delete(`${API}/it-talents/${deleteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchItTalents();
      } else if (deleteType === 'admin') {
        // deleteId for admins is the email (no separate uuid)
        await axios.delete(`${API}/admins/${encodeURIComponent(deleteId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchAdmins();
      } else if (deleteType === 'client') {
        await axios.delete(`${API}/clients/${deleteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchClients();
      } else if (deleteType === 'availability') {
        await axios.delete(`${API}/availability-options/${deleteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchAvailabilityOptions();
      }
      toast.success(t('deleteSuccess'));
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setDeleteId(null);
    }
  };

  const handleAddRecruiter = async () => {
    if (!newRecruiterEmail) {
      toast.error(lang === 'pt' ? 'Insira o email' : 'Enter email');
      return;
    }
    try {
      await axios.post(`${API}/recruiters`, { email: newRecruiterEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setShowAddRecruiterDialog(false);
      setNewRecruiterEmail('');
      fetchRecruiters();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleAddITTalent = async () => {
    if (!newITTalentEmail) {
      toast.error(lang === 'pt' ? 'Insira o email' : 'Enter email');
      return;
    }
    try {
      await axios.post(`${API}/it-talents`, { email: newITTalentEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setShowAddITTalentDialog(false);
      setNewITTalentEmail('');
      fetchItTalents();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail) {
      toast.error(lang === 'pt' ? 'Insira o email' : 'Enter email');
      return;
    }
    try {
      await axios.post(`${API}/admins`, { email: newAdminEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setShowAddAdminDialog(false);
      setNewAdminEmail('');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleAddClient = async () => {
    if (!newClientName) {
      toast.error(lang === 'pt' ? 'Insira o nome do cliente' : 'Enter client name');
      return;
    }
    try {
      await axios.post(`${API}/clients`, { name: newClientName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setShowAddClientDialog(false);
      setNewClientName('');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleUpdateClient = async () => {
    if (!editClient) return;
    try {
      await axios.put(`${API}/clients/${editClient.id}`, {
        name: editClient.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setEditClient(null);
      fetchClients();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleAddAvailability = async () => {
    if (!newAvailability.name_pt || !newAvailability.name_en) {
      toast.error(lang === 'pt' ? 'Preencha todos os campos' : 'Fill all fields');
      return;
    }
    try {
      await axios.post(`${API}/availability-options`, newAvailability, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setShowAddAvailabilityDialog(false);
      setNewAvailability({ name_pt: '', name_en: '', order: availabilityOptions.length + 1 });
      fetchAvailabilityOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleUpdateAvailability = async () => {
    if (!editAvailability) return;
    try {
      await axios.put(`${API}/availability-options/${editAvailability.id}`, {
        name_pt: editAvailability.name_pt,
        name_en: editAvailability.name_en,
        order: editAvailability.order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('saveSuccess'));
      setEditAvailability(null);
      fetchAvailabilityOptions();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleSeedAvailability = async () => {
    try {
      await axios.post(`${API}/admin/seed-availability-options`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(lang === 'pt' ? 'Opções padrão criadas' : 'Default options created');
      fetchAvailabilityOptions();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const filteredCompetencies = competencies.filter(comp => 
    comp.name_pt.toLowerCase().includes(search.toLowerCase()) ||
    comp.name_en.toLowerCase().includes(search.toLowerCase()) ||
    comp.category.toLowerCase().includes(search.toLowerCase()) ||
    (comp.keywords || []).some(k => k.toLowerCase().includes(search.toLowerCase()))
  );

  const categories = [
    { value: 'technical', labelPt: 'Técnica', labelEn: 'Technical' },
    { value: 'soft', labelPt: 'Soft Skill', labelEn: 'Soft Skill' },
    { value: 'language', labelPt: 'Linguagem', labelEn: 'Language' },
    { value: 'tool', labelPt: 'Ferramenta', labelEn: 'Tool' },
    { value: 'framework', labelPt: 'Framework', labelEn: 'Framework' },
    { value: 'other', labelPt: 'Outra', labelEn: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] flex" data-testid="backoffice-page">
      {/* Sidebar */}
      <aside className="backoffice-sidebar hidden md:block" data-testid="sidebar">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#1E3C3C] flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-lg">Evolutt</span>
        </div>

        <nav className="space-y-2">
          <div className={`backoffice-link cursor-pointer ${activeTab === 'competencies' ? 'active' : ''}`} onClick={() => setActiveTab('competencies')} data-testid="nav-competencies">
            <Award className="w-5 h-5" />
            {lang === 'pt' ? 'Competências' : 'Competencies'}
          </div>
          <div className={`backoffice-link cursor-pointer ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')} data-testid="nav-clients">
            <Building2 className="w-5 h-5" />
            {lang === 'pt' ? 'Clientes' : 'Clients'}
          </div>
          <div className={`backoffice-link cursor-pointer ${activeTab === 'recruiters' ? 'active' : ''}`} onClick={() => setActiveTab('recruiters')} data-testid="nav-recruiters">
            <Users className="w-5 h-5" />
            {lang === 'pt' ? 'Recrutadores' : 'Recruiters'}
          </div>
          <div className={`backoffice-link cursor-pointer ${activeTab === 'it-talents' ? 'active' : ''}`} onClick={() => setActiveTab('it-talents')} data-testid="nav-it-talents">
            <UserCheck className="w-5 h-5" />
            IT Talents
          </div>
          <div className={`backoffice-link cursor-pointer ${activeTab === 'admins' ? 'active' : ''}`} onClick={() => setActiveTab('admins')} data-testid="nav-admins">
            <Shield className="w-5 h-5" />
            {lang === 'pt' ? 'Administradores' : 'Administrators'}
          </div>
          <div className={`backoffice-link cursor-pointer ${activeTab === 'requests' ? 'active' : ''}`} onClick={() => setActiveTab('requests')} data-testid="nav-requests">
            <Bell className="w-5 h-5" />
            {lang === 'pt' ? 'Pedidos Pendentes' : 'Pending Requests'}
            {pendingRequests.length > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{pendingRequests.length}</span>
            )}
          </div>
          <button
            onClick={() => navigate('/')}
            className="backoffice-link w-full text-left"
            data-testid="nav-home"
          >
            <LayoutDashboard className="w-5 h-5" />
            {lang === 'pt' ? 'Voltar ao Site' : 'Back to Site'}
          </button>
        </nav>

        <div className="mt-auto pt-8 border-t border-white/10 mt-8">
          <div className="px-4 py-3 text-sm">
            <p className="text-white/60">{lang === 'pt' ? 'Sessão iniciada como' : 'Logged in as'}</p>
            <p className="text-white font-medium truncate">{user?.email}</p>
          </div>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="backoffice-link w-full text-left text-red-400 hover:text-red-300"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8" data-testid="main-content">
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="competencies" className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                {lang === 'pt' ? 'Competências' : 'Competencies'}
              </TabsTrigger>
              <TabsTrigger value="clients" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {lang === 'pt' ? 'Clientes' : 'Clients'}
              </TabsTrigger>
              <TabsTrigger value="availability" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {lang === 'pt' ? 'Disponibilidade' : 'Availability'}
              </TabsTrigger>
              <TabsTrigger value="functions" className="flex items-center gap-2" data-testid="functions-tab">
                <Briefcase className="w-4 h-4" />
                {lang === 'pt' ? 'Funções IT' : 'IT Functions'}
              </TabsTrigger>
              <TabsTrigger value="recruiters" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                {lang === 'pt' ? 'Recrutadores' : 'Recruiters'}
              </TabsTrigger>
              <TabsTrigger value="it-talents" className="flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                IT Talents
              </TabsTrigger>
              <TabsTrigger value="admins" className="flex items-center gap-2" data-testid="admins-tab">
                <Shield className="w-4 h-4" />
                {lang === 'pt' ? 'Administradores' : 'Administrators'}
              </TabsTrigger>
              <TabsTrigger value="requests" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                {lang === 'pt' ? 'Pedidos' : 'Requests'}
                {pendingRequests.length > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-1.5">{pendingRequests.length}</span>}
              </TabsTrigger>
            </TabsList>

            {/* Competencies Tab */}
            <TabsContent value="competencies">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {t('competencyManagement')}
                  </h1>
                  <p className="text-[#64748b]">
                    {competencies.length} {lang === 'pt' ? 'competências registadas' : 'competencies registered'}
                    {duplicateGroups.length > 0 && (
                      <span className="ml-2 text-amber-700">
                        · <span className="font-medium">{duplicateGroups.length}</span>{' '}
                        {lang === 'pt' ? 'grupos de duplicados' : 'duplicate groups'}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {lang === 'pt' ? 'Template Excel' : 'Excel template'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => importFileInputRef.current?.click()}
                    data-testid="import-excel-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {lang === 'pt' ? 'Importar Excel' : 'Import Excel'}
                  </Button>
                  <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    onChange={handleImportExcel}
                    data-testid="import-file-input"
                  />
                  <Button
                    className="btn-primary"
                    onClick={() => setShowAddDialog(true)}
                    data-testid="add-competency-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addNewCompetency')}
                  </Button>
                </div>
              </div>

              {/* Duplicate warning banner */}
              {duplicateGroups.length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="duplicates-banner">
                  <h3 className="font-medium text-amber-900 mb-1 flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    {lang === 'pt' ? 'Competências duplicadas detetadas' : 'Duplicate competencies detected'}
                  </h3>
                  <p className="text-sm text-amber-800 mb-2">
                    {lang === 'pt'
                      ? 'Estas competências existem mais do que uma vez (ignorando maiúsculas/minúsculas). Considere remover os duplicados:'
                      : 'These competencies exist more than once (ignoring case). Consider removing duplicates:'}
                  </p>
                  <ul className="text-sm text-amber-900 space-y-1">
                    {duplicateGroups.slice(0, 5).map((g) => (
                      <li key={g.key}>
                        <span className="font-mono text-xs px-1.5 py-0.5 bg-amber-200 rounded">{g.key}</span>
                        {' → '}
                        {g.items.map((c) => c.name_pt).join(' · ')}
                      </li>
                    ))}
                    {duplicateGroups.length > 5 && (
                      <li className="italic text-amber-700">
                        + {duplicateGroups.length - 5} {lang === 'pt' ? 'mais' : 'more'}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Search */}
              <div className="mb-6">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                  <Input
                    placeholder={lang === 'pt' ? 'Pesquisar competências...' : 'Search competencies...'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Table */}
              <Card data-testid="competencies-table">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : filteredCompetencies.length === 0 ? (
                    <div className="text-center py-16 text-[#64748b]">
                      {lang === 'pt' ? 'Nenhuma competência encontrada' : 'No competencies found'}
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('namePT')}</th>
                          <th>{t('nameEN')}</th>
                          <th>{t('keywords')}</th>
                          <th>{t('category')}</th>
                          <th className="w-32">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCompetencies.map((comp) => (
                          <tr key={comp.id} data-testid={`comp-row-${comp.id}`}>
                            <td className="font-medium">{comp.name_pt}</td>
                            <td>{comp.name_en}</td>
                            <td>
                              <div className="flex flex-wrap gap-1">
                                {(comp.keywords || []).slice(0, 3).map((kw, i) => (
                                  <span key={`kw-${kw}-${i}`} className="text-xs bg-[#f0fdfa] text-[#1E3C3C] px-2 py-1 rounded">
                                    {kw}
                                  </span>
                                ))}
                                {(comp.keywords || []).length > 3 && (
                                  <span className="text-xs text-[#64748b]">+{comp.keywords.length - 3}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="badge-primary">
                                {categories.find(c => c.value === comp.category)?.[lang === 'pt' ? 'labelPt' : 'labelEn'] || comp.category}
                              </span>
                            </td>
                            <td>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditComp({
                                    ...comp,
                                    keywords: (comp.keywords || []).join(', ')
                                  })}
                                  data-testid={`edit-comp-${comp.id}`}
                                >
                                  <Edit className="w-4 h-4 text-[#1E3C3C]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => { setDeleteId(comp.id); setDeleteType('competency'); }}
                                  data-testid={`delete-comp-${comp.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Recruiters Tab */}
            <TabsContent value="recruiters">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {lang === 'pt' ? 'Gestão de Recrutadores' : 'Recruiter Management'}
                  </h1>
                  <p className="text-[#64748b]">
                    {recruiters.length} {lang === 'pt' ? 'recrutadores registados' : 'recruiters registered'}
                  </p>
                </div>
                <Button
                  className="btn-primary"
                  onClick={() => setShowAddRecruiterDialog(true)}
                  data-testid="add-recruiter-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('addRecruiter')}
                </Button>
              </div>

              <Card data-testid="recruiters-table">
                <CardContent className="p-0">
                  {recruiters.length === 0 ? (
                    <div className="text-center py-16 text-[#64748b]">
                      {lang === 'pt' ? 'Nenhum recrutador registado' : 'No recruiters registered'}
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>{lang === 'pt' ? 'Data de Registo' : 'Registration Date'}</th>
                          <th className="w-32">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recruiters.map((recruiter) => (
                          <tr key={recruiter.id} data-testid={`recruiter-row-${recruiter.id}`}>
                            <td className="font-medium flex items-center gap-2">
                              <Mail className="w-4 h-4 text-[#64748b]" />
                              {recruiter.email}
                            </td>
                            <td>{new Date(recruiter.created_at).toLocaleDateString()}</td>
                            <td>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => { setDeleteId(recruiter.id); setDeleteType('recruiter'); }}
                                data-testid={`delete-recruiter-${recruiter.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              <div className="mt-6 p-4 bg-[#f0fdfa] rounded-lg">
                <h3 className="font-medium text-[#1E3C3C] mb-2">
                  {lang === 'pt' ? 'Como funciona?' : 'How does it work?'}
                </h3>
                <p className="text-sm text-[#64748b]">
                  {lang === 'pt' 
                    ? 'Adicione emails de recrutadores. Quando esses utilizadores se registarem, terão automaticamente a role de recrutador (sem acesso ao backoffice).'
                    : 'Add recruiter emails. When these users register, they will automatically have the recruiter role (no backoffice access).'
                  }
                </p>
              </div>
            </TabsContent>

            {/* Clients Tab */}
            <TabsContent value="clients">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {lang === 'pt' ? 'Gestão de Clientes' : 'Client Management'}
                  </h1>
                  <p className="text-[#64748b]">
                    {clients.length} {lang === 'pt' ? 'clientes registados' : 'clients registered'}
                  </p>
                </div>
                <Button
                  className="btn-primary"
                  onClick={() => setShowAddClientDialog(true)}
                  data-testid="add-client-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {lang === 'pt' ? 'Adicionar Cliente' : 'Add Client'}
                </Button>
              </div>

              <Card data-testid="clients-table">
                <CardContent className="p-0">
                  {clients.length === 0 ? (
                    <div className="text-center py-16 text-[#64748b]">
                      {lang === 'pt' ? 'Nenhum cliente registado' : 'No clients registered'}
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{lang === 'pt' ? 'Nome' : 'Name'}</th>
                          <th>{lang === 'pt' ? 'Data de Registo' : 'Registration Date'}</th>
                          <th className="w-32">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.map((client) => (
                          <tr key={client.id} data-testid={`client-row-${client.id}`}>
                            <td className="font-medium flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-[#64748b]" />
                              {client.name}
                            </td>
                            <td>{new Date(client.created_at).toLocaleDateString()}</td>
                            <td>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditClient(client)}
                                  data-testid={`edit-client-${client.id}`}
                                >
                                  <Edit className="w-4 h-4 text-[#1E3C3C]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => { setDeleteId(client.id); setDeleteType('client'); }}
                                  data-testid={`delete-client-${client.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Availability Options Tab */}
            <TabsContent value="availability">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {lang === 'pt' ? 'Gestão de Disponibilidade' : 'Availability Management'}
                  </h1>
                  <p className="text-[#64748b]">
                    {availabilityOptions.length} {lang === 'pt' ? 'opções configuradas' : 'options configured'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {availabilityOptions.length === 0 && (
                    <Button
                      variant="outline"
                      onClick={handleSeedAvailability}
                      data-testid="seed-availability-btn"
                    >
                      {lang === 'pt' ? 'Criar Opções Padrão' : 'Create Default Options'}
                    </Button>
                  )}
                  <Button
                    className="btn-primary"
                    onClick={() => {
                      setNewAvailability({ name_pt: '', name_en: '', order: availabilityOptions.length + 1 });
                      setShowAddAvailabilityDialog(true);
                    }}
                    data-testid="add-availability-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {lang === 'pt' ? 'Adicionar Opção' : 'Add Option'}
                  </Button>
                </div>
              </div>

              <Card data-testid="availability-table">
                <CardContent className="p-0">
                  {availabilityOptions.length === 0 ? (
                    <div className="text-center py-16 text-[#64748b]">
                      <Clock className="w-12 h-12 mx-auto mb-4 text-[#94a3b8]" />
                      <p>{lang === 'pt' ? 'Nenhuma opção configurada' : 'No options configured'}</p>
                      <p className="text-sm mt-2">
                        {lang === 'pt' 
                          ? 'Clique em "Criar Opções Padrão" para adicionar as opções iniciais'
                          : 'Click "Create Default Options" to add initial options'
                        }
                      </p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{lang === 'pt' ? 'Ordem' : 'Order'}</th>
                          <th>{lang === 'pt' ? 'Português' : 'Portuguese'}</th>
                          <th>{lang === 'pt' ? 'Inglês' : 'English'}</th>
                          <th className="w-32">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availabilityOptions.map((option) => (
                          <tr key={option.id} data-testid={`availability-row-${option.id}`}>
                            <td className="font-medium">{option.order}</td>
                            <td className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-[#64748b]" />
                              {option.name_pt}
                            </td>
                            <td>{option.name_en}</td>
                            <td>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditAvailability(option)}
                                  data-testid={`edit-availability-${option.id}`}
                                >
                                  <Edit className="w-4 h-4 text-[#64748b]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => { setDeleteId(option.id); setDeleteType('availability'); }}
                                  data-testid={`delete-availability-${option.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              <div className="mt-6 p-4 bg-[#f0fdfa] rounded-lg">
                <h3 className="font-medium text-[#1E3C3C] mb-2">
                  {lang === 'pt' ? 'Como funciona?' : 'How does it work?'}
                </h3>
                <p className="text-sm text-[#64748b]">
                  {lang === 'pt' 
                    ? 'Configure as opções de disponibilidade que aparecerão no dropdown do formulário de CV. A ordem define a sequência de apresentação.'
                    : 'Configure the availability options that will appear in the CV form dropdown. The order defines the display sequence.'
                  }
                </p>
              </div>
            </TabsContent>

            {/* IT Functions Tab */}
            <TabsContent value="functions">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {lang === 'pt' ? 'Gestão de Funções IT' : 'IT Functions Management'}
                  </h1>
                  <p className="text-sm text-[#64748b] mt-1">
                    {lang === 'pt' ? 'Gerir a lista de funções IT disponíveis no CV' : 'Manage available IT functions for CV'}
                  </p>
                </div>
              </div>

              {/* Add new function */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label>{lang === 'pt' ? 'Nome (PT)' : 'Name (PT)'}</Label>
                      <Input
                        value={newFunctionPt}
                        onChange={(e) => setNewFunctionPt(e.target.value)}
                        placeholder={lang === 'pt' ? 'Ex: Desenvolvedor Backend' : 'E.g.: Backend Developer'}
                        className="mt-1"
                        data-testid="new-function-pt-input"
                      />
                    </div>
                    <div className="flex-1">
                      <Label>{lang === 'pt' ? 'Nome (EN)' : 'Name (EN)'}</Label>
                      <Input
                        value={newFunctionEn}
                        onChange={(e) => setNewFunctionEn(e.target.value)}
                        placeholder={lang === 'pt' ? 'Ex: Backend Developer' : 'E.g.: Backend Developer'}
                        className="mt-1"
                        data-testid="new-function-en-input"
                      />
                    </div>
                    <Button onClick={handleAddFunction} className="btn-primary" data-testid="add-function-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      {lang === 'pt' ? 'Adicionar' : 'Add'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Functions list */}
              <div className="grid gap-2">
                {itFunctions.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#e2e8f0] hover:border-[#1E3C3C]/30 transition-colors" data-testid={`function-row-${f.id}`}>
                    <div>
                      <span className="font-medium text-[#0f172a]">{f.name_pt}</span>
                      <span className="text-sm text-[#64748b] ml-3">({f.name_en})</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-400 hover:text-red-600"
                      onClick={() => handleDeleteFunction(f.id)}
                      data-testid={`delete-function-${f.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {itFunctions.length === 0 && (
                  <p className="text-center text-[#64748b] py-8">
                    {lang === 'pt' ? 'Nenhuma função registada' : 'No functions registered'}
                  </p>
                )}
              </div>
            </TabsContent>


            {/* IT Talents Tab */}
            <TabsContent value="it-talents">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {lang === 'pt' ? 'Gestão de IT Talents' : 'IT Talent Management'}
                  </h1>
                  <p className="text-[#64748b]">
                    {itTalents.length} {lang === 'pt' ? 'IT talents registados' : 'IT talents registered'}
                  </p>
                </div>
                <Button
                  className="btn-primary"
                  onClick={() => setShowAddITTalentDialog(true)}
                  data-testid="add-it-talent-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {lang === 'pt' ? 'Adicionar IT Talent' : 'Add IT Talent'}
                </Button>
              </div>

              <Card data-testid="it-talents-table">
                <CardContent className="p-0">
                  {itTalents.length === 0 ? (
                    <div className="text-center py-16 text-[#64748b]">
                      {lang === 'pt' ? 'Nenhum IT talent registado' : 'No IT talents registered'}
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>{lang === 'pt' ? 'Data de Registo' : 'Registration Date'}</th>
                          <th className="w-32">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itTalents.map((talent) => (
                          <tr key={talent.id} data-testid={`it-talent-row-${talent.id}`}>
                            <td className="font-medium flex items-center gap-2">
                              <UserCheck className="w-4 h-4 text-[#64748b]" />
                              {talent.email}
                            </td>
                            <td>{new Date(talent.created_at).toLocaleDateString()}</td>
                            <td>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => { setDeleteId(talent.id); setDeleteType('it_talent'); }}
                                data-testid={`delete-it-talent-${talent.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              <div className="mt-6 p-4 bg-[#FFFBEB] rounded-lg border border-[#FFD700]/30">
                <h3 className="font-medium text-[#B8860B] mb-2">
                  {lang === 'pt' ? 'Como funciona?' : 'How does it work?'}
                </h3>
                <p className="text-sm text-[#64748b]">
                  {lang === 'pt' 
                    ? 'Adicione emails de IT Talents. Quando esses utilizadores se registarem, terão automaticamente a role de IT Talent.'
                    : 'Add IT Talent emails. When these users register, they will automatically have the IT Talent role.'
                  }
                </p>
              </div>
            </TabsContent>

            <TabsContent value="admins">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {lang === 'pt' ? 'Gestão de Administradores' : 'Administrator Management'}
                  </h1>
                  <p className="text-[#64748b]">
                    {admins.length} {lang === 'pt' ? 'administradores registados' : 'administrators registered'}
                  </p>
                </div>
                <Button
                  className="btn-primary"
                  onClick={() => setShowAddAdminDialog(true)}
                  data-testid="add-admin-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {lang === 'pt' ? 'Adicionar Administrador' : 'Add Administrator'}
                </Button>
              </div>

              <Card data-testid="admins-table">
                <CardContent className="p-0">
                  {admins.length === 0 ? (
                    <div className="text-center py-16 text-[#64748b]">
                      {lang === 'pt' ? 'Nenhum administrador registado' : 'No administrators registered'}
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>{lang === 'pt' ? 'Data de Registo' : 'Registration Date'}</th>
                          <th className="w-32">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {admins.map((adm) => (
                          <tr key={adm.email} data-testid={`admin-row-${adm.email}`}>
                            <td className="font-medium flex items-center gap-2">
                              <Shield className="w-4 h-4 text-[#C9A84C]" />
                              {adm.email}
                              {adm.protected && (
                                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-[#FEF3C7] text-[#B8860B] border border-[#FFD700]/40">
                                  {lang === 'pt' ? 'Protegido' : 'Protected'}
                                </span>
                              )}
                            </td>
                            <td>{adm.created_at ? new Date(adm.created_at).toLocaleDateString() : '-'}</td>
                            <td>
                              {adm.protected ? (
                                <span className="text-xs text-[#94a3b8]">
                                  {lang === 'pt' ? 'Não removível' : 'Not removable'}
                                </span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => { setDeleteId(adm.email); setDeleteType('admin'); }}
                                  data-testid={`delete-admin-${adm.email}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              <div className="mt-6 p-4 bg-[#FFFBEB] rounded-lg border border-[#FFD700]/30">
                <h3 className="font-medium text-[#B8860B] mb-2">
                  {lang === 'pt' ? 'Como funciona?' : 'How does it work?'}
                </h3>
                <p className="text-sm text-[#64748b]">
                  {lang === 'pt' 
                    ? 'Adicione emails de administradores. Se já existir um utilizador com esse email, é imediatamente promovido a administrador. Caso contrário, ao registar-se receberá automaticamente o papel de administrador. Os administradores protegidos não podem ser removidos.'
                    : 'Add administrator emails. If a user with that email already exists, they are immediately promoted to administrator. Otherwise, on registration they automatically receive the administrator role. Protected administrators cannot be removed.'
                  }
                </p>
              </div>
            </TabsContent>

            <TabsContent value="requests">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-[#0f172a]">
                    {lang === 'pt' ? 'Pedidos de Novas Competências' : 'New Competency Requests'}
                  </h1>
                  <p className="text-[#64748b]">
                    {pendingRequests.length} {lang === 'pt' ? 'pedidos pendentes' : 'pending requests'}
                  </p>
                </div>
              </div>
              
              {pendingRequests.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-[#64748b]">
                  {lang === 'pt' ? 'Sem pedidos pendentes' : 'No pending requests'}
                </CardContent></Card>
              ) : (
                <div className="space-y-3">
                  {pendingRequests.map(req => (
                    <Card key={req.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-semibold text-lg">{req.name_pt}</span>
                              <span className="text-xs bg-[#f1f5f9] px-2 py-1 rounded">{req.category}</span>
                            </div>
                            <p className="text-sm text-[#64748b] mt-1">
                              {lang === 'pt' ? 'Solicitado por' : 'Requested by'}: {req.requested_by} • {new Date(req.created_at).toLocaleDateString()}
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                              <Input
                                value={renameInput[req.id] || ''}
                                onChange={(e) => setRenameInput({...renameInput, [req.id]: e.target.value})}
                                placeholder={lang === 'pt' ? 'Renomear (opcional)' : 'Rename (optional)'}
                                className="max-w-xs text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={async () => {
                                try {
                                  const body = { action: 'approve' };
                                  if (renameInput[req.id]) {
                                    body.new_name_pt = renameInput[req.id];
                                    body.new_name_en = renameInput[req.id];
                                  }
                                  await axios.put(`${API}/competency-requests/${req.id}`, body, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success(lang === 'pt' ? 'Competência aprovada!' : 'Competency approved!');
                                  fetchPendingRequests();
                                  fetchCompetencies();
                                } catch (e) { toast.error(t('error')); }
                              }}
                              data-testid={`approve-${req.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              {lang === 'pt' ? 'Aprovar' : 'Approve'}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/competency-requests/${req.id}`, { action: 'reject' }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success(lang === 'pt' ? 'Pedido rejeitado' : 'Request rejected');
                                  fetchPendingRequests();
                                } catch (e) { toast.error(t('error')); }
                              }}
                              data-testid={`reject-${req.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              {lang === 'pt' ? 'Rejeitar' : 'Reject'}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Add Competency Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-dialog">
          <DialogHeader>
            <DialogTitle>{t('addNewCompetency')}</DialogTitle>
            <DialogDescription>
              {lang === 'pt' ? 'Adicione uma nova competência ao sistema' : 'Add a new competency to the system'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('namePT')}</Label>
              <Input
                value={newComp.name_pt}
                onChange={(e) => setNewComp({ ...newComp, name_pt: e.target.value })}
                placeholder="React.js"
                className="mt-1"
                data-testid="new-comp-name-pt"
              />
            </div>
            <div>
              <Label>{t('nameEN')}</Label>
              <Input
                value={newComp.name_en}
                onChange={(e) => setNewComp({ ...newComp, name_en: e.target.value })}
                placeholder="React.js"
                className="mt-1"
                data-testid="new-comp-name-en"
              />
            </div>
            <div>
              <Label>{t('keywords')}</Label>
              <Input
                value={newComp.keywords}
                onChange={(e) => setNewComp({ ...newComp, keywords: e.target.value })}
                placeholder={t('keywordsPlaceholder')}
                className="mt-1"
                data-testid="new-comp-keywords"
              />
              <p className="text-xs text-[#64748b] mt-1">
                {lang === 'pt' ? 'Ex: frontend, biblioteca, javascript' : 'Ex: frontend, library, javascript'}
              </p>
            </div>
            <div>
              <Label>{t('category')}</Label>
              <Select
                value={newComp.category}
                onValueChange={(value) => setNewComp({ ...newComp, category: value })}
              >
                <SelectTrigger className="mt-1" data-testid="new-comp-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {lang === 'pt' ? cat.labelPt : cat.labelEn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} data-testid="cancel-add-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleAddCompetency} data-testid="confirm-add-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Competency Dialog */}
      <Dialog open={!!editComp} onOpenChange={() => setEditComp(null)}>
        <DialogContent data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>{t('edit')} {lang === 'pt' ? 'Competência' : 'Competency'}</DialogTitle>
          </DialogHeader>
          {editComp && (
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('namePT')}</Label>
                <Input
                  value={editComp.name_pt}
                  onChange={(e) => setEditComp({ ...editComp, name_pt: e.target.value })}
                  className="mt-1"
                  data-testid="edit-comp-name-pt"
                />
              </div>
              <div>
                <Label>{t('nameEN')}</Label>
                <Input
                  value={editComp.name_en}
                  onChange={(e) => setEditComp({ ...editComp, name_en: e.target.value })}
                  className="mt-1"
                  data-testid="edit-comp-name-en"
                />
              </div>
              <div>
                <Label>{t('keywords')}</Label>
                <Input
                  value={editComp.keywords}
                  onChange={(e) => setEditComp({ ...editComp, keywords: e.target.value })}
                  placeholder={t('keywordsPlaceholder')}
                  className="mt-1"
                  data-testid="edit-comp-keywords"
                />
              </div>
              <div>
                <Label>{t('category')}</Label>
                <Select
                  value={editComp.category}
                  onValueChange={(value) => setEditComp({ ...editComp, category: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-comp-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {lang === 'pt' ? cat.labelPt : cat.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditComp(null)} data-testid="cancel-edit-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleUpdateCompetency} data-testid="confirm-edit-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Recruiter Dialog */}
      <Dialog open={showAddRecruiterDialog} onOpenChange={setShowAddRecruiterDialog}>
        <DialogContent data-testid="add-recruiter-dialog">
          <DialogHeader>
            <DialogTitle>{t('addRecruiter')}</DialogTitle>
            <DialogDescription>
              {lang === 'pt' 
                ? 'Adicione o email do recrutador. Quando se registar, terá automaticamente a role de recrutador.'
                : 'Add recruiter email. When they register, they will automatically have recruiter role.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>{t('recruiterEmail')}</Label>
            <Input
              type="email"
              value={newRecruiterEmail}
              onChange={(e) => setNewRecruiterEmail(e.target.value)}
              placeholder="recruiter@company.com"
              className="mt-1"
              data-testid="new-recruiter-email"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRecruiterDialog(false)} data-testid="cancel-recruiter-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleAddRecruiter} data-testid="confirm-recruiter-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add IT Talent Dialog */}
      <Dialog open={showAddITTalentDialog} onOpenChange={setShowAddITTalentDialog}>
        <DialogContent data-testid="add-it-talent-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'pt' ? 'Adicionar IT Talent' : 'Add IT Talent'}</DialogTitle>
            <DialogDescription>
              {lang === 'pt' 
                ? 'Adicione o email do IT Talent. Quando se registar, terá automaticamente a role de IT Talent.'
                : 'Add IT Talent email. When they register, they will automatically have IT Talent role.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Email</Label>
            <Input
              type="email"
              value={newITTalentEmail}
              onChange={(e) => setNewITTalentEmail(e.target.value)}
              placeholder="talent@company.com"
              className="mt-1"
              data-testid="new-it-talent-email"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddITTalentDialog(false)} data-testid="cancel-it-talent-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleAddITTalent} data-testid="confirm-it-talent-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog open={showAddAdminDialog} onOpenChange={setShowAddAdminDialog}>
        <DialogContent data-testid="add-admin-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'pt' ? 'Adicionar Administrador' : 'Add Administrator'}</DialogTitle>
            <DialogDescription>
              {lang === 'pt'
                ? 'Adicione o email do administrador. Se o utilizador já existir, será imediatamente promovido. Caso contrário, terá o papel de administrador ao registar-se.'
                : 'Add the administrator email. If the user already exists, they will be promoted immediately. Otherwise, they will receive the administrator role upon registration.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Email</Label>
            <Input
              type="email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              placeholder="admin@empresa.com"
              className="mt-1"
              data-testid="new-admin-email"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdminDialog(false)} data-testid="cancel-admin-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleAddAdmin} data-testid="confirm-admin-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent data-testid="add-client-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'pt' ? 'Adicionar Cliente' : 'Add Client'}</DialogTitle>
            <DialogDescription>
              {lang === 'pt' 
                ? 'Adicione um novo cliente ao sistema.'
                : 'Add a new client to the system.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>{lang === 'pt' ? 'Nome do Cliente' : 'Client Name'}</Label>
            <Input
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              placeholder={lang === 'pt' ? 'Nome da empresa' : 'Company name'}
              className="mt-1"
              data-testid="new-client-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClientDialog(false)} data-testid="cancel-client-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleAddClient} data-testid="confirm-client-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={!!editClient} onOpenChange={() => setEditClient(null)}>
        <DialogContent data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'pt' ? 'Editar Cliente' : 'Edit Client'}</DialogTitle>
          </DialogHeader>
          {editClient && (
            <div className="py-4">
              <Label>{lang === 'pt' ? 'Nome do Cliente' : 'Client Name'}</Label>
              <Input
                value={editClient.name}
                onChange={(e) => setEditClient({ ...editClient, name: e.target.value })}
                className="mt-1"
                data-testid="edit-client-name"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditClient(null)} data-testid="cancel-edit-client-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleUpdateClient} data-testid="confirm-edit-client-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Availability Option Dialog */}
      <Dialog open={showAddAvailabilityDialog} onOpenChange={setShowAddAvailabilityDialog}>
        <DialogContent data-testid="add-availability-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'pt' ? 'Adicionar Opção de Disponibilidade' : 'Add Availability Option'}</DialogTitle>
            <DialogDescription>
              {lang === 'pt' 
                ? 'Adicione uma nova opção de disponibilidade ao sistema.'
                : 'Add a new availability option to the system.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{lang === 'pt' ? 'Texto em Português' : 'Portuguese Text'}</Label>
              <Input
                value={newAvailability.name_pt}
                onChange={(e) => setNewAvailability({ ...newAvailability, name_pt: e.target.value })}
                placeholder={lang === 'pt' ? 'Ex: Imediata' : 'Ex: Immediate'}
                className="mt-1"
                data-testid="new-availability-name-pt"
              />
            </div>
            <div>
              <Label>{lang === 'pt' ? 'Texto em Inglês' : 'English Text'}</Label>
              <Input
                value={newAvailability.name_en}
                onChange={(e) => setNewAvailability({ ...newAvailability, name_en: e.target.value })}
                placeholder={lang === 'pt' ? 'Ex: Immediate' : 'Ex: Immediate'}
                className="mt-1"
                data-testid="new-availability-name-en"
              />
            </div>
            <div>
              <Label>{lang === 'pt' ? 'Ordem' : 'Order'}</Label>
              <Input
                type="number"
                min="1"
                value={newAvailability.order}
                onChange={(e) => setNewAvailability({ ...newAvailability, order: parseInt(e.target.value) || 1 })}
                className="mt-1"
                data-testid="new-availability-order"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAvailabilityDialog(false)} data-testid="cancel-availability-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleAddAvailability} data-testid="confirm-availability-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Availability Option Dialog */}
      <Dialog open={!!editAvailability} onOpenChange={() => setEditAvailability(null)}>
        <DialogContent data-testid="edit-availability-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'pt' ? 'Editar Opção de Disponibilidade' : 'Edit Availability Option'}</DialogTitle>
          </DialogHeader>
          {editAvailability && (
            <div className="space-y-4 py-4">
              <div>
                <Label>{lang === 'pt' ? 'Texto em Português' : 'Portuguese Text'}</Label>
                <Input
                  value={editAvailability.name_pt}
                  onChange={(e) => setEditAvailability({ ...editAvailability, name_pt: e.target.value })}
                  className="mt-1"
                  data-testid="edit-availability-name-pt"
                />
              </div>
              <div>
                <Label>{lang === 'pt' ? 'Texto em Inglês' : 'English Text'}</Label>
                <Input
                  value={editAvailability.name_en}
                  onChange={(e) => setEditAvailability({ ...editAvailability, name_en: e.target.value })}
                  className="mt-1"
                  data-testid="edit-availability-name-en"
                />
              </div>
              <div>
                <Label>{lang === 'pt' ? 'Ordem' : 'Order'}</Label>
                <Input
                  type="number"
                  min="1"
                  value={editAvailability.order}
                  onChange={(e) => setEditAvailability({ ...editAvailability, order: parseInt(e.target.value) || 1 })}
                  className="mt-1"
                  data-testid="edit-availability-order"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAvailability(null)} data-testid="cancel-edit-availability-btn">
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleUpdateAvailability} data-testid="confirm-edit-availability-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {lang === 'pt' 
                ? 'Esta ação não pode ser revertida.'
                : 'This action cannot be undone.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
